import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';

interface Agency { id?: string; code: string; name: string; active?: boolean; }

interface Props { onClose: () => void; }

export function ManageAgenciesModal({ onClose }: Props) {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: agencies = [] } = useQuery({ queryKey: ['agencies'], queryFn: () => api.get<Agency[]>('/agencies').then((r) => r.data) });

  const add = useMutation({
    mutationFn: async () => api.post('/agencies', { code: code.trim().toUpperCase(), name: name.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agencies'] });
      setCode('');
      setName('');
      toast.success('Agency added', code.toUpperCase());
    },
    onError: () => toast.error('Add failed', 'Code must be unique'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-hdr" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div className="modal-title">⚙ Manage Agencies</div>
          <button className="modal-close close-btn" onClick={onClose}>✕</button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>
          Agencies can be made inactive but never deleted — this protects historical timesheet data.
        </div>
        <div id="agency-list-body">
          {agencies.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 12, textAlign: 'center', padding: 14 }}>No agencies yet.</div>
          ) : agencies.map((a) => (
            <div key={a.code} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 8, marginBottom: 5, background: 'var(--surface)', borderRadius: 6 }}>
              <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 12, color: 'var(--accent)', minWidth: 50 }}>{a.code}</span>
              <span style={{ fontSize: 12, flex: 1 }}>{a.name}</span>
              {a.active === false ? <span style={{ fontSize: 9, color: 'var(--muted)' }}>inactive</span> : null}
            </div>
          ))}
        </div>
        <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>Add New Agency</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input className="form-input" id="new-agency-code" placeholder="Code (e.g. XYZ)" maxLength={10} value={code} onChange={(e) => setCode(e.target.value)} style={{ width: 100, fontSize: 11 }} />
            <input className="form-input" id="new-agency-name" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1, fontSize: 11 }} />
            <button className="btn btn-primary btn-sm" onClick={() => add.mutate()} disabled={add.isPending || !code.trim() || !name.trim()}>+ Add</button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
